import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { EndpointBase } from './endpoint-base.service';
import { ConfigurationService } from './configuration.service';

export interface AgentToolInvocation {
  tool: string;
  input: string;
  outputPreview: string;
  latencyMs: number;
  success: boolean;
}

export interface AgentTurnResponse {
  assistantMessage: string;
  toolInvocations: AgentToolInvocation[];
  usage: { inputTokens: number; outputTokens: number; cachedInputTokens: number };
  finishReason: string;
}

export interface AgentConversation {
  conversationId: string;
  agentName: string;
  createdDate: string;
}

export interface AgentFormHint {
  hint: string;
  severity: 'info' | 'warning' | 'error' | 'none';
}

export interface PreSubmitIssue {
  fieldName: string;
  label: string;
  issue: string;
  severity: 'error' | 'warning';
  suggestedFix: string | null;
  canAutoFix: boolean;
}

@Injectable()
export class AgentService extends EndpointBase {

  get adminQueryConversationsUrl() { return this.configurations.baseUrl + '/api/agents/admin-query/conversations'; }
  get formAssistHintUrl() { return this.configurations.baseUrl + '/api/agents/form-assist/hint'; }
  get preSubmitReviewUrl() { return this.configurations.baseUrl + '/api/agents/form-assist/pre-submit-review'; }
  get caseSummaryUrl() { return this.configurations.baseUrl + '/api/agents/case-summary'; }

  constructor(private configurations: ConfigurationService, http: HttpClient, authService: AuthService) {
    super(http, authService);
  }

  createAdminQueryConversation(): Observable<AgentConversation> {
    return this.http.post<AgentConversation>(this.adminQueryConversationsUrl, {}, this.requestHeaders).pipe(
      catchError(err => this.handleError(err, () => this.createAdminQueryConversation()))
    );
  }

  sendAdminQueryMessage(conversationId: string, message: string): Observable<AgentTurnResponse> {
    const url = `${this.adminQueryConversationsUrl}/${conversationId}/messages`;
    return this.http.post<AgentTurnResponse>(url, { message }, this.requestHeaders).pipe(
      catchError(err => this.handleError(err, () => this.sendAdminQueryMessage(conversationId, message)))
    );
  }

  getFormHint(fieldName: string, fieldValue: string, formContext: Record<string, string> = {}): Observable<AgentFormHint> {
    return this.http.post<AgentFormHint>(this.formAssistHintUrl, { fieldName, fieldValue, formContext: JSON.stringify(formContext) }, this.requestHeaders).pipe(
      catchError(err => this.handleError(err, () => this.getFormHint(fieldName, fieldValue, formContext)))
    );
  }

  getPreSubmitReview(formSnapshot: Record<string, string>): Observable<{ issues: PreSubmitIssue[] }> {
    return this.http.post<{ issues: PreSubmitIssue[] }>(this.preSubmitReviewUrl, { formSnapshot }, this.requestHeaders).pipe(
      catchError(err => this.handleError(err, () => this.getPreSubmitReview(formSnapshot)))
    );
  }

  getCaseSummary(caseSnapshot: Record<string, string>): Observable<{ summary: string }> {
    return this.http.post<{ summary: string }>(this.caseSummaryUrl, { caseSnapshot }, this.requestHeaders).pipe(
      catchError(err => this.handleError(err, () => this.getCaseSummary(caseSnapshot)))
    );
  }
}
